import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, BadgeCheck, ShieldCheck } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const VendorCompliancePanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ business_name: "", legal_registration: "", tax_id: "", country: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/vendor-compliance/mine`, headers()).then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(load, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/vendor-compliance/submit`, form, headers());
      toast.success(res.status === "submitted" ? "Compliance check submitted — FUNZ will review it." : res.note || "Submitted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (data === null) return null;
  const c = data.compliance;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="compliance-panel">
      <p className="flex items-center gap-2 font-display text-lg font-bold">
        <ShieldCheck className="h-5 w-5 text-[#22C55E]" /> Vendor Compliance
        {c?.status === "approved" && <span className="flex items-center gap-1 rounded-full bg-[#22C55E]/10 px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#22C55E]" data-testid="compliance-verified"><BadgeCheck className="h-3 w-3" /> Verified Business</span>}
      </p>
      {c && c.status !== "approved" ? (
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
          <p className="text-xs font-bold">{c.business_name}</p>
          <p className="mt-0.5 font-jbmono text-[10px] text-slate-500">{c.legal_registration} · {c.tax_id} · {c.country}</p>
          <p className={`mt-2 rounded-full px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider w-fit ${
            c.status === "rejected" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#F97316]/10 text-[#F97316]"
          }`} data-testid="compliance-status">{c.status}{c.sanctions_flag ? " · sanctions flagged" : ""}</p>
          {c.review_note && <p className="mt-2 text-xs text-slate-400">{c.review_note}</p>}
        </div>
      ) : !c && (
        <div className="mt-4 space-y-3" data-testid="compliance-form">
          <div className="grid gap-2 sm:grid-cols-2">
            <input data-testid="vc-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Legal business name"
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
            <input data-testid="vc-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Country"
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
            <input data-testid="vc-registration" value={form.legal_registration} onChange={(e) => setForm({ ...form, legal_registration: e.target.value })} placeholder="Business registration no."
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
            <input data-testid="vc-tax" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="Tax / GST identification"
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
          </div>
          <button data-testid="vc-submit" onClick={submit} disabled={busy || !form.business_name.trim() || !form.legal_registration.trim() || !form.tax_id.trim() || !form.country.trim()}
            className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
            {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : "Submit for compliance review"}
          </button>
          <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600">Requires a verified Government ID first · sanctions screening is automatic</p>
        </div>
      )}
    </section>
  );
};
